/**
 * Privacy Policy — ProfitSpot
 */
import React, { useState, useEffect } from 'react';
import { Lock, ArrowLeft, ChevronRight, Database, Eye, EyeOff, Share2, Clock, Cookie, Shield, User, RefreshCw, Mail, Globe, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  { id: 'overview',      title: 'Overview',                   icon: Eye },
  { id: 'collect',       title: 'Information We Collect',     icon: Database },
  { id: 'not-collect',   title: 'What We Do NOT Collect',     icon: EyeOff },
  { id: 'use',           title: 'How We Use Your Data',       icon: Globe },
  { id: 'third-party',   title: 'Third-Party Services',       icon: Share2 },
  { id: 'retention',     title: 'Data Retention',             icon: Clock },
  { id: 'cookies',       title: 'Cookies & Storage',          icon: Cookie },
  { id: 'security',      title: 'Security',                   icon: Shield },
  { id: 'rights',        title: 'Your Rights',                icon: User },
  { id: 'breach',        title: 'Data Breach Response',       icon: AlertTriangle },
  { id: 'children',      title: 'Children',                   icon: User },
  { id: 'changes',       title: 'Changes to This Policy',     icon: RefreshCw },
  { id: 'contact',       title: 'Contact Us',                 icon: Mail },
];

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-8 border-b border-gray-800/50 pb-8 mb-8 last:border-0">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-lg bg-[#00d4ff]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#00d4ff]" />
      </div>
      <h2 className="text-base font-bold text-white">{title}</h2>
    </div>
    <div className="text-gray-400 text-sm leading-relaxed space-y-3 pl-11">
      {children}
    </div>
  </section>
);

const DataRow = ({ label, value, highlight }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-800/40 last:border-0">
    <span className="text-gray-500 text-xs w-32 flex-shrink-0">{label}</span>
    <span className={`text-xs flex-1 ${highlight ? 'text-[#00ff88] font-medium' : 'text-gray-300'}`}>{value}</span>
  </div>
);

const Callout = ({ color = 'blue', children }) => {
  const styles = {
    blue:  'bg-[#00d4ff]/5 border-[#00d4ff]/20 text-[#00d4ff]',
    teal:  'bg-[#00ff88]/5 border-[#00ff88]/20 text-[#00ff88]',
    amber: 'bg-amber-500/5 border-amber-500/20 text-amber-400',
    red:   'bg-red-500/5 border-red-500/20 text-red-400',
  };
  return <div className={`border rounded-lg p-4 ${styles[color]}`}>{children}</div>;
};

export default function Privacy() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      {/* Hero header */}
      <div className="border-b border-gray-800 bg-[#0f0f1a]">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors text-sm"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#00d4ff]/10 rounded-xl border border-[#00d4ff]/20">
              <Lock className="w-7 h-7 text-[#00d4ff]" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
              <p className="text-gray-500 text-sm">Last updated: March 2026 &nbsp;·&nbsp; Applies to all ProfitSpot services</p>
            </div>
          </div>

          {/* Quick summary */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'We collect', value: 'Email, name, usage patterns', color: '#00d4ff' },
              { label: 'We never sell', value: 'Your personal data', color: '#00ff88' },
              { label: 'You control', value: 'Access, correction & deletion', color: '#ffa502' },
            ].map(item => (
              <div key={item.label} className="bg-[#0a0a12] border border-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                <p className="text-sm font-semibold" style={{ color: item.color }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 flex gap-10">
        {/* Sticky Table of Contents — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-8">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map(({ id, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded transition-colors ${
                    activeSection === id
                      ? 'text-[#00d4ff] bg-[#00d4ff]/10'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {activeSection === id && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                  <span className={activeSection === id ? 'ml-0' : 'ml-5'}>{title}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-6 mb-8 text-sm text-gray-400 leading-relaxed">
            At <strong className="text-white">ProfitSpot</strong>, we believe privacy is fundamental.
            This policy explains exactly what data we collect, why we collect it, how we protect it,
            and the rights you have over it. We use plain language — no legalese.
          </div>

          <Section id="overview" title="Overview" icon={Eye}>
            <p>ProfitSpot is a DeFi yield intelligence platform. We collect the minimum data necessary to provide the Service. Here's the complete picture:</p>
            <div className="bg-[#0a0a12] rounded-lg border border-gray-800 p-4 mt-3">
              <DataRow label="Account data"   value="Email address, display name, hashed password" />
              <DataRow label="Payment data"   value="Managed by Stripe — we never see card numbers" />
              <DataRow label="Usage data"     value="Features used, pages visited, API request counts" />
              <DataRow label="Notifications"  value="Email alerts if you enable them (opt-in)" />
              <DataRow label="Wallet data"    value="Only addresses you voluntarily provide for tracking" />
              <DataRow label="Private keys"   value="Never collected, never stored" highlight />
              <DataRow label="Seed phrases"   value="Never collected, never stored" highlight />
              <DataRow label="Sold to others" value="Never" highlight />
            </div>
          </Section>

          <Section id="collect" title="Information We Collect" icon={Database}>
            <p><strong className="text-white">Account Information</strong></p>
            <p>When you register, we collect your email address, chosen display name, and a bcrypt-hashed version of your password. We never store your password in plain text.</p>

            <p className="mt-3"><strong className="text-white">Subscription & Billing</strong></p>
            <p>Payments are processed by <strong className="text-white">Stripe</strong>. We receive and store your Stripe customer ID and subscription status. We never receive, process, or store your full credit card number, CVV, or banking details — these stay entirely within Stripe's PCI-DSS-compliant infrastructure.</p>

            <p className="mt-3"><strong className="text-white">Usage Data</strong></p>
            <p>We collect anonymized usage patterns (which features you use, how often, API response times) to improve the Service and prioritize development. This data is not linked to personally identifiable information.</p>

            <p className="mt-3"><strong className="text-white">Email Preferences</strong></p>
            <p>If you enable Smart Yield Alerts (Pro+), we store your email notification preference and the timestamp of the last alert sent. Alert emails are sent via Resend with a 6-hour cooldown to prevent spam.</p>

            <p className="mt-3"><strong className="text-white">Wallet Addresses (optional)</strong></p>
            <p>If you choose to track a wallet address for personalized analytics, that address is stored associated with your account. Wallet addresses are public blockchain data — we do not have access to the private keys controlling those addresses.</p>
          </Section>

          <Section id="not-collect" title="What We Do NOT Collect" icon={EyeOff}>
            <Callout color="teal">
              <p className="font-semibold text-sm mb-2">ProfitSpot is 100% non-custodial. We never touch your crypto.</p>
            </Callout>
            <p>We explicitly <strong className="text-white">do not</strong> collect, request, or store:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Private keys or wallet seed phrases</li>
              <li>Full credit card or banking account numbers</li>
              <li>Government-issued ID or KYC documents</li>
              <li>Your location (IP addresses are not logged persistently)</li>
              <li>Third-party social login data (we use our own auth system)</li>
              <li>Device fingerprints or biometric data</li>
            </ul>
          </Section>

          <Section id="use" title="How We Use Your Data" icon={Globe}>
            <p>We use the data we collect strictly to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><strong className="text-white">Provide the Service</strong> — authenticate sessions, serve tier-appropriate features</li>
              <li><strong className="text-white">Process payments</strong> — manage subscriptions via Stripe</li>
              <li><strong className="text-white">Send transactional emails</strong> — trial start, subscription receipts, alerts</li>
              <li><strong className="text-white">Send yield alerts</strong> — only for Pro+ users who have opted in</li>
              <li><strong className="text-white">Improve the platform</strong> — aggregate usage analytics</li>
              <li><strong className="text-white">Prevent fraud</strong> — detect unusual access patterns</li>
              <li><strong className="text-white">Comply with law</strong> — retain records as legally required</li>
            </ul>
            <Callout color="teal">
              <p className="text-sm font-semibold">We do not sell, rent, or trade your personal data to any third party — ever.</p>
            </Callout>
          </Section>

          <Section id="third-party" title="Third-Party Services" icon={Share2}>
            <p>We integrate with the following third-party services. Each link below leads to their privacy policy:</p>
            <div className="space-y-2 mt-2">
              {[
                { name: 'Stripe',         role: 'Payment processing',            data: 'Payment details only — never shared with us' },
                { name: 'Resend',         role: 'Transactional email delivery',  data: 'Your email address (for alert delivery)' },
                { name: 'MongoDB Atlas',  role: 'Database hosting',              data: 'All account & subscription data (encrypted at rest)' },
                { name: 'DeFiLlama',      role: 'DeFi yield data (public API)',  data: 'No personal data transmitted' },
                { name: 'Moralis',        role: 'Blockchain data (whale tracker)',data: 'Wallet addresses you choose to track (public data)' },
                { name: 'GoPlus Security',role: 'Token security scores',         data: 'Token contract addresses only (public data)' },
              ].map(s => (
                <div key={s.name} className="bg-[#0a0a12] border border-gray-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-medium">{s.name}</span>
                    <span className="text-xs text-gray-600">{s.role}</span>
                  </div>
                  <p className="text-xs text-gray-500">Data shared: {s.data}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="retention" title="Data Retention" icon={Clock}>
            <div className="bg-[#0a0a12] rounded-lg border border-gray-800 p-4">
              <DataRow label="Account data"   value="Retained while account is active" />
              <DataRow label="Payment records" value="7 years (legal/tax compliance)" />
              <DataRow label="Usage analytics" value="12 months rolling (anonymized)" />
              <DataRow label="Alert logs"      value="30 days" />
              <DataRow label="Deleted accounts" value="Purged within 30 days of deletion request" />
            </div>
            <p className="mt-3">You may request deletion of your account and all associated data at any time by emailing <strong className="text-white">support@profitspot.live</strong>. We will process deletion requests within 30 days, except where retention is legally required.</p>
          </Section>

          <Section id="cookies" title="Cookies & Local Storage" icon={Cookie}>
            <p>ProfitSpot uses minimal browser storage to function. We do <strong className="text-white">not</strong> use third-party tracking cookies, advertising pixels, or analytics trackers (e.g., Google Analytics).</p>
            <p>We use <strong className="text-white">localStorage</strong> for:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Your JWT authentication token (to keep you logged in)</li>
              <li>UI preferences (e.g., sidebar collapsed state, risk acknowledgment)</li>
            </ul>
            <p>These are first-party, session-functional items only. You can clear them at any time via your browser's developer tools or by logging out.</p>
          </Section>

          <Section id="security" title="Security" icon={Shield}>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><strong className="text-white">bcrypt</strong> password hashing (salted, industry-standard rounds)</li>
              <li><strong className="text-white">JWT token authentication</strong> with short-lived tokens</li>
              <li><strong className="text-white">HTTPS/TLS encryption</strong> for all data in transit</li>
              <li><strong className="text-white">MongoDB encryption at rest</strong></li>
              <li><strong className="text-white">Private hardware infrastructure</strong> — Nighton (Mac Mini) + Balinas operate on our own hardware with restricted network access</li>
              <li><strong className="text-white">Role-based access control</strong> — only admin accounts can access platform analytics or user data</li>
            </ul>
            <Callout color="amber">
              <p className="text-sm">While we implement strong security practices, no system is completely impenetrable. We recommend using a unique, strong password for your ProfitSpot account.</p>
            </Callout>
          </Section>

          <Section id="rights" title="Your Rights" icon={User}>
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
            <div className="space-y-2 mt-2">
              {[
                { right: 'Access',      desc: 'Request a copy of all personal data we hold about you' },
                { right: 'Correction',  desc: 'Request correction of inaccurate or incomplete data' },
                { right: 'Deletion',    desc: 'Request erasure of your data ("right to be forgotten")' },
                { right: 'Portability', desc: 'Request your data in a machine-readable format' },
                { right: 'Objection',   desc: 'Object to processing of your data for certain purposes' },
                { right: 'Opt-out',     desc: 'Unsubscribe from all marketing/alert emails at any time' },
              ].map(item => (
                <div key={item.right} className="flex items-start gap-3 bg-[#0a0a12] border border-gray-800 rounded-lg p-3">
                  <span className="text-[#00d4ff] font-bold text-xs w-20 flex-shrink-0 mt-0.5">{item.right}</span>
                  <span className="text-gray-400 text-xs">{item.desc}</span>
                </div>
              ))}
            </div>
            <p className="mt-3">To exercise any of these rights, email <strong className="text-white">support@profitspot.live</strong>. We respond to all requests within 30 days.</p>
          </Section>

          <Section id="breach" title="Data Breach Response" icon={AlertTriangle}>
            <p>In the event of a data breach affecting your personal information, we will:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Notify affected users via email within 72 hours of discovery</li>
              <li>Describe what data was affected and what we are doing about it</li>
              <li>Notify relevant regulatory authorities where required by law</li>
              <li>Take immediate steps to contain and remediate the breach</li>
            </ul>
          </Section>

          <Section id="children" title="Children" icon={User}>
            <p>ProfitSpot is not directed at or intended for children under 18 years of age. We do not knowingly collect personal data from minors.</p>
            <p>If you are a parent or guardian and believe your child has provided us with personal data, please contact us immediately at <strong className="text-white">support@profitspot.live</strong> and we will delete such data promptly.</p>
          </Section>

          <Section id="changes" title="Changes to This Policy" icon={RefreshCw}>
            <p>We may update this Privacy Policy to reflect changes in the Service, data practices, or applicable law. When we make material changes, we will:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Update the "Last updated" date at the top of this page</li>
              <li>Send an email notification to your registered address</li>
              <li>Display an in-app notice for 30 days</li>
            </ul>
            <p>Continued use of the Service after policy updates constitutes your acceptance of the revised Policy.</p>
          </Section>

          <Section id="contact" title="Contact Us" icon={Mail}>
            <p>For any privacy-related questions, concerns, data access requests, or deletion requests:</p>
            <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800 mt-2">
              <p className="text-white font-medium">ProfitSpot Privacy Team</p>
              <p className="text-[#00d4ff] mt-1">support@profitspot.live</p>
              <p className="text-gray-500 text-xs mt-2">We aim to respond to all privacy requests within 30 days.</p>
            </div>
          </Section>
        </main>
      </div>
    </div>
  );
}
